import { action, computed, makeObservable, observable } from 'mobx';

import type { CreateTarget } from 'actions';
import { Aggregate } from 'edit/Aggregate';
import type { EditableEntity } from 'edit/EditableEntity';
import type { FolderReference } from 'edit/reference/EditFolderReference';
import type { ZoneReference } from 'edit/reference/EditZoneReference';
import type { SchedulingWindow } from 'api/scheduling_windows/endpoints/getSchedulingWindow';
import type { SaveSchedulingWindow } from 'api/scheduling_windows/endpoints/postSchedulingWindow';
import { formatFolderPath } from 'helpers/folderPaths';

import { EditableSchedule } from './EditableSchedule';
import { EditableWindowLink } from './EditableWindowLink';

/**
 * Last-resort timezone a blank create form starts on, when the caller supplies no
 * configured default (see `options.defaultTimezone`).
 */
const FALLBACK_TIMEZONE = 'UTC';

export interface EditableSchedulingWindowOptions {
  /** Pre-fill from a loaded record but treat as a new record (POST, cleared name). */
  readonly duplicate?: boolean;
  /** Workspace create pre-target: seeds the zone + folder of a blank create form. */
  readonly createIn?: CreateTarget;
  /**
   * Timezone a blank create form starts on — the `default_timezone` config value,
   * resolved by the caller. Passed in rather than read here: the entity must not
   * reach into redux, and the config's shape is the caller's problem.
   */
  readonly defaultTimezone?: string;
}

/**
 * A scheduling window open for editing: its own fields plus **two** one-to-many
 * aggregates — the opening rules (`schedules`) and the links to other windows
 * (`links`). Built directly from the loaded wire record (or `null` for a blank
 * create form); the model *is* the editable state, projecting straight back to
 * the request body via `toSave`.
 *
 * `original` is the pre-edit identity — the zone and name the PUT addresses and
 * the create-vs-update branch keys on. `null` means a not-yet-saved record; it
 * flips to a real identity after the first successful save, via `reinitialize`
 * with the server's response.
 *
 * A blank create form starts with **no** rules and no links, which is what the
 * previous form did and what the backend accepts (a window with no rules never
 * opens, which is a legitimate thing to save and then fill in).
 */
export class EditableSchedulingWindow implements EditableEntity<SchedulingWindow | null> {
  description: string | null = null;
  folder: FolderReference | null = null;
  private readonly _links = new Aggregate<EditableSchedulingWindow, EditableWindowLink>(this, (aggregate) => new EditableWindowLink(aggregate));
  name: string | null = null;
  private _original: { zoneName: string; name: string } | null = null;
  private readonly _schedules = new Aggregate<EditableSchedulingWindow, EditableSchedule>(this, (aggregate) => new EditableSchedule(aggregate));
  private _snapshot = '';
  /**
   * The window's own timezone, which its rules inherit unless they override it.
   *
   * Nullable although the column is `NOT NULL`: the field is a searchable picker
   * and so can be emptied, and a form state the control can reach has to be one
   * the model can hold — pinning it to the last valid zone would make the field
   * impossible to clear and retype. Saving it empty is a 400 the dialog shows,
   * the server being the authority on what a window requires.
   */
  timezone: string | null = FALLBACK_TIMEZONE;
  zone: ZoneReference | null = null;

  constructor(loaded: SchedulingWindow | null, options?: EditableSchedulingWindowOptions) {
    makeObservable<EditableSchedulingWindow, '_original'>(this, {
      description: observable,
      folder: observable.ref,
      name: observable,
      _original: observable.ref,
      timezone: observable,
      zone: observable.ref,
      isDirty: computed,
    });
    this._init(loaded, options?.duplicate ?? false, options?.createIn, options?.defaultTimezone);
  }

  get isCreating(): boolean {
    return this._original === null;
  }

  get isDirty(): boolean {
    return this._original === null || JSON.stringify(this.toSave()) !== this._snapshot;
  }

  /** The links to other windows, in display order. */
  get links(): readonly EditableWindowLink[] {
    return this._links.children;
  }

  /**
   * Pre-edit identity (`null` while creating). The `save` callback reads this to
   * choose create vs update and to address the PUT, which is by zone and name
   * even though the single read is by numeric id.
   */
  get original(): { zoneName: string; name: string } | null {
    return this._original;
  }

  /** The opening rules, in display order. */
  get schedules(): readonly EditableSchedule[] {
    return this._schedules.children;
  }

  /** Append a fresh, empty link and return it. */
  createLink = (): EditableWindowLink => this._links.create();

  /** Append a fresh, empty opening rule and return it. */
  createSchedule = (): EditableSchedule => this._schedules.create();

  /** Move a link to a new position (see `Aggregate.move` for the index contract). */
  moveLink = (fromIndex: number, toIndex: number): void => {
    this._links.move(fromIndex, toIndex);
  };

  /** Move an opening rule to a new position. Order is display-only — the backend stores no rank. */
  moveSchedule = (fromIndex: number, toIndex: number): void => {
    this._schedules.move(fromIndex, toIndex);
  };

  /**
   * Reset to the loaded record (or a blank create form when `null`) and recapture
   * the dirty baseline. Called after a successful load or save.
   */
  reinitialize = (loaded: SchedulingWindow | null): void => {
    this._init(loaded);
  };

  toSave(): SaveSchedulingWindow {
    return {
      name: this.name,
      description: this.description,
      timezone: this.timezone,
      zone_name: this.zone?.name ?? null,
      folder_id: this.folder?.folder_id ?? null,
      schedules: this._schedules.children.map((schedule) => schedule.toSave()),
      // A link with no window picked is dropped rather than sent — an empty row
      // is a half-finished edit, not an instruction. Matches the previous form's
      // `uiLinksToApiRefs`, which filtered on the window name. Rules are NOT
      // filtered: an empty rule is a meaningful "always open".
      references: this._links.children.filter((link) => link.window !== null).map((link) => link.toSave()),
    };
  }

  // Shared by the constructor and `reinitialize`. Rebuilds both aggregates in
  // place (same instances, so reactivity survives) and snapshots the resulting
  // save body for dirty tracking. Wrapped in `action` so the whole rebuild lands
  // as one transaction: observers see the final state, never a half-applied
  // intermediate, and re-render once rather than once per write.
  private readonly _init = action((loaded: SchedulingWindow | null, duplicate = false, createIn?: CreateTarget, defaultTimezone?: string): void => {
    this._schedules.clear();
    this._links.clear();

    if (loaded !== null) {
      if (loaded.zone_name === null) {
        throw new Error(`Scheduling window '${loaded.name}' has no zone (orphaned by zone deletion); cannot be edited.`);
      }
      if (duplicate) {
        this._original = null;
        this.name = null;
      } else {
        this._original = { zoneName: loaded.zone_name, name: loaded.name };
        this.name = loaded.name;
      }
      this.zone = { name: loaded.zone_name };
      this.folder = loaded.folder_id !== null ? { folder_id: loaded.folder_id, path: formatFolderPath(loaded.folder_path) } : null;
      this.description = loaded.description;
      this.timezone = loaded.timezone;
      for (const wire of loaded.schedules) {
        this._schedules.append(new EditableSchedule(this._schedules, wire));
      }
      for (const wire of loaded.references) {
        this._links.append(new EditableWindowLink(this._links, wire));
      }
    } else {
      this._original = null;
      this.name = null;
      // Seed the zone/folder from the workspace create pre-target when present.
      this.zone = createIn ? { name: createIn.zoneName } : null;
      this.folder = createIn?.folderId != null ? { folder_id: createIn.folderId, path: '' } : null;
      this.description = null;
      this.timezone = defaultTimezone || FALLBACK_TIMEZONE;
    }

    this._snapshot = JSON.stringify(this.toSave());
  });
}
